import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type { DiscoverInput, DiscoveryResult, DiscoveryRunSummary } from "./types";

/**
 * POST /api/campaigns/{id}/discovery — run a discovery pass for the campaign.
 * On success the lead review queue, the campaigns list (its counts change) and
 * this campaign's run history are invalidated so all reflect the fresh run.
 *
 * ADR-0023: the mutation accepts an optional AbortSignal so the Discovery screen
 * can offer a Cancel while a (possibly auto-started) run is in flight. An aborted
 * run rejects with an AbortError, which the caller treats as "cancelled", not a failure.
 */
export function useRunDiscovery(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ signal, ...input }: DiscoverInput & { signal?: AbortSignal }) =>
      httpClient<DiscoveryResult>(`/api/campaigns/${campaignId}/discovery`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
        signal,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      // The run advanced campaign.brief/lastSource server-side — refresh the detail too.
      qc.invalidateQueries({ queryKey: ["campaign", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["discovery-runs", getActiveTenantId(), campaignId] });
      qc.invalidateQueries({ queryKey: ["discovery-runs-feed", getActiveTenantId()] });
    },
  });
}

/** GET /api/campaigns/{id}/discovery/runs — this campaign's run history (newest first). */
export function useDiscoveryRuns(campaignId: string, limit = 20) {
  return useQuery({
    queryKey: ["discovery-runs", getActiveTenantId(), campaignId] as const,
    queryFn: () =>
      httpClient<DiscoveryRunSummary[]>(`/api/campaigns/${campaignId}/discovery/runs?limit=${limit}`),
    enabled: !!campaignId,
  });
}

/** GET /api/discovery/runs — recent discovery runs across all campaigns (activity feed). */
export function useRecentDiscoveryRuns(limit = 20) {
  return useQuery({
    queryKey: ["discovery-runs-feed", getActiveTenantId()] as const,
    queryFn: () => httpClient<DiscoveryRunSummary[]>(`/api/discovery/runs?limit=${limit}`),
  });
}
